import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TimeSeriesChartCard from "../../../shared/components/charts/TimeSeriesChartCard";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { useStatsOverviewQuery } from "../hooks/useStats";
import {
  IntegrationActiveIcon,
  IntegrationDisabledIcon,
  IntegrationPossibleIcon,
  StatsCard,
  StatsFilters,
  TIMEFRAME_OPTIONS,
} from "./subcomponents";

const formatPeriodLabel = (
  iso: string,
  granularity: "day" | "week" | "month",
) => {
  const date = new Date(iso);
  if (granularity === "week") {
    const end = new Date(date);
    end.setUTCDate(end.getUTCDate() + 6);
    return `${format(date, "dd/MM", { locale: es })} - ${format(end, "dd/MM", { locale: es })}`;
  }
  if (granularity === "month") {
    return format(date, "MMM yy", { locale: es });
  }
  return format(date, "dd/MM/yy", { locale: es });
};

const integrationPalette = {
  border: "rgb(168, 85, 247)",
  background: "rgba(168, 85, 247, 0.18)",
  point: "rgb(192, 132, 252)",
} as const;

const usersPalette = {
  border: "rgb(236, 72, 153)",
  background: "rgba(236, 72, 153, 0.2)",
  point: "rgb(244, 114, 182)",
} as const;

const workspacePalette = {
  border: "rgb(59, 130, 246)",
  background: "rgba(59, 130, 246, 0.18)",
  point: "rgb(96, 165, 250)",
} as const;

const emailPalette = {
  border: "rgb(34, 197, 94)",
  background: "rgba(34, 197, 94, 0.18)",
  point: "rgb(74, 222, 128)",
} as const;

const INTEGRATION_DISTRIBUTION: Record<string, number> = {
  all: 1,
  twitch: 0.42,
  youtube: 0.36,
  kick: 0.22,
};

const paletteByTab = {
  integrations: integrationPalette,
  users: usersPalette,
  workspaces: workspacePalette,
  emails: emailPalette,
};

const tabs = [
  { id: "integrations" as const, label: "Integraciones" },
  { id: "users" as const, label: "Usuarios" },
  { id: "workspaces" as const, label: "Workspaces" },
  { id: "emails" as const, label: "Emails" },
];

// Threshold above which the Users tab surfaces a red spike banner. Mirrors the
// backend RegistrationSpikeScheduler's default. Tuned for the post-incident
// reality (the 2026-05-15 attack ran at 24/min; baseline is ~0/min).
const SPIKE_BANNER_THRESHOLD = 5;

type StatsTab = "integrations" | "users" | "workspaces" | "emails";

const StatsPage = () => {
  const [activeTab, setActiveTab] = useState<StatsTab>("integrations");
  const [timeframe, setTimeframe] = useState<"daily" | "weekly" | "monthly">(
    "daily",
  );
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [timeRange, setTimeRange] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [selectedIntegration, setSelectedIntegration] = useState("all");
  const { data, isLoading, isFetching } = useStatsOverviewQuery();

  const applyTemporalFilters = useCallback(
    (points: { period: string; count: number }[]) => {
      return points.filter((point) => {
        const pointDate = new Date(point.period);
        if (dateRange.start) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          if (pointDate < startDate) return false;
        }
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          if (pointDate > endDate) return false;
        }
        const minutesValue = pointDate.getHours() * 60 + pointDate.getMinutes();
        if (timeRange.start) {
          const [startHours, startMinutes] = timeRange.start
            .split(":")
            .map(Number);
          const startMinutesValue = startHours * 60 + startMinutes;
          if (minutesValue < startMinutesValue) return false;
        }
        if (timeRange.end) {
          const [endHours, endMinutes] = timeRange.end.split(":").map(Number);
          const endMinutesValue = endHours * 60 + endMinutes;
          if (minutesValue > endMinutesValue) return false;
        }
        return true;
      });
    },
    [dateRange.end, dateRange.start, timeRange.end, timeRange.start],
  );

  const buildChartPoints = useCallback(
    (
      points: { period: string; count: number }[],
      granularity: "day" | "week" | "month",
      multiplier = 1,
    ) => {
      const filtered = applyTemporalFilters(points);
      return filtered.map((point) => ({
        label: formatPeriodLabel(point.period, granularity),
        value: Math.round(point.count * multiplier),
      }));
    },
    [applyTemporalFilters],
  );

  const integrationsCards = useMemo(() => {
    if (!data) return [];
    return [
      {
        title: "Integraciones posibles",
        value: data.integrations.available ?? data.integrations.total,
        icon: <IntegrationPossibleIcon />,
      },
      {
        title: "Integraciones activas",
        value: data.integrations.active,
        icon: <IntegrationActiveIcon />,
      },
      {
        title: "Integraciones deshabilitadas",
        value: data.integrations.inactive,
        icon: <IntegrationDisabledIcon />,
      },
    ];
  }, [data]);

  const integrationsSeries = useMemo(() => {
    if (!data) return { daily: [], weekly: [], monthly: [] };
    const multiplier = INTEGRATION_DISTRIBUTION[selectedIntegration] ?? 1;
    return {
      daily: buildChartPoints(data.integrations.daily, "day", multiplier),
      weekly: buildChartPoints(data.integrations.weekly, "week", multiplier),
      monthly: buildChartPoints(data.integrations.monthly, "month", multiplier),
    };
  }, [data, buildChartPoints, selectedIntegration]);

  const usersSeries = useMemo(() => {
    if (!data) return { daily: [], weekly: [], monthly: [] };
    return {
      daily: buildChartPoints(data.users.daily, "day"),
      weekly: buildChartPoints(data.users.weekly, "week"),
      monthly: buildChartPoints(data.users.monthly, "month"),
    };
  }, [data, buildChartPoints]);

  const workspacesSeries = useMemo(() => {
    if (!data) return { daily: [], weekly: [], monthly: [] };
    return {
      daily: buildChartPoints(data.workspaces.daily, "day"),
      weekly: buildChartPoints(data.workspaces.weekly, "week"),
      monthly: buildChartPoints(data.workspaces.monthly, "month"),
    };
  }, [data, buildChartPoints]);

  const emailsSeries = useMemo(() => {
    if (!data) return { daily: [], weekly: [], monthly: [] };
    return {
      daily: buildChartPoints(data.emails.daily, "day"),
      weekly: buildChartPoints(data.emails.weekly, "week"),
      monthly: buildChartPoints(data.emails.monthly, "month"),
    };
  }, [data, buildChartPoints]);

  const usersCards = useMemo(() => {
    if (!data) return [];
    return [
      { title: "Usuarios totales", value: data.users.total },
      { title: "Perfiles públicos", value: data.users.publicProfiles },
      { title: "Perfiles privados", value: data.users.privateProfiles },
      { title: "Sesiones activas", value: data.users.activeSessions },
      { title: "Sin sesión activa", value: data.users.inactiveSessions },
    ];
  }, [data]);

  const workspacesCards = useMemo(() => {
    if (!data) return [];
    return [
      { title: "Workspaces totales", value: data.workspaces.total },
      { title: "Workspaces activos", value: data.workspaces.active },
      { title: "Workspaces inactivos", value: data.workspaces.inactive },
      { title: "Públicos", value: data.workspaces.public },
      { title: "Privados", value: data.workspaces.private },
    ];
  }, [data]);

  const emailsCards = useMemo(() => {
    if (!data) return [];
    return [
      { title: "Emails enviados", value: data.emails.totalSent },
      { title: "Pendientes", value: data.emails.totalPending },
      { title: "Fallidos", value: data.emails.totalFailed },
    ];
  }, [data]);

  const showSpikeBanner =
    data != null &&
    data.users.spikeRatio >= SPIKE_BANNER_THRESHOLD &&
    activeTab === "users";

  const refreshedAtText = useMemo(() => {
    if (!data) return "Actualizado en tiempo real.";
    const latestPeriod =
      data.integrations.daily.at(-1)?.period ?? data.users.daily.at(-1)?.period;
    if (!latestPeriod) return "Sin registros recientes.";
    return `Datos al ${formatDistanceToNow(new Date(latestPeriod), { addSuffix: true, locale: es })}`;
  }, [data]);

  const isBusy = isLoading || isFetching;

  const seriesByTab = {
    integrations: integrationsSeries,
    users: usersSeries,
    workspaces: workspacesSeries,
    emails: emailsSeries,
  };

  const timeframeConfig =
    TIMEFRAME_OPTIONS.find((option) => option.id === timeframe) ??
    TIMEFRAME_OPTIONS[0];
  const chartPalette = paletteByTab[activeTab];
  const currentSeries = seriesByTab[activeTab][timeframe];

  const cardsByTab = {
    integrations: integrationsCards,
    users: usersCards,
    workspaces: workspacesCards,
    emails: emailsCards,
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Estadísticas" subtitle={refreshedAtText} />

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as StatsTab)}
        className="w-full"
      >
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {showSpikeBanner && data ? (
        <div
          role="alert"
          className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-100"
        >
          <div className="font-semibold text-red-50">
            🚨 Spike de registros detectado
          </div>
          <div className="mt-1 text-red-200/80">
            <strong>{data.users.lastHour}</strong> registros en la última hora —{" "}
            <strong>{data.users.spikeRatio.toFixed(1)}×</strong> el promedio de
            los últimos 7 días ({data.users.avgPerHourLast7d.toFixed(2)} /
            hora). Revisá logs del backend para descartar abuso de{" "}
            <code className="font-mono">/auth/register</code>.
          </div>
        </div>
      ) : null}

      <section className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
          {cardsByTab[activeTab].map((card) => (
            <StatsCard
              key={card.title}
              title={card.title}
              value={card.value}
              icon={"icon" in card ? card.icon : undefined}
            />
          ))}
        </div>

        <StatsFilters
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          selectedIntegration={selectedIntegration}
          onIntegrationChange={setSelectedIntegration}
          showIntegrationFilter={activeTab === "integrations"}
        />

        <TimeSeriesChartCard
          title={timeframeConfig.label}
          points={currentSeries}
          palette={chartPalette}
        />

        {activeTab === "emails" && data ? (
          <div className="overflow-hidden rounded-lg border border-border/40">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Template</th>
                  <th className="px-4 py-2 text-right">Enviados</th>
                  <th className="px-4 py-2 text-right">Pendientes</th>
                  <th className="px-4 py-2 text-right">Fallidos</th>
                </tr>
              </thead>
              <tbody>
                {data.emails.byTemplate.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-3 text-center text-muted-foreground"
                      colSpan={4}
                    >
                      Sin envíos registrados.
                    </td>
                  </tr>
                ) : (
                  data.emails.byTemplate.map((row) => (
                    <tr
                      key={row.template}
                      className="border-t border-border/30 hover:bg-muted/20"
                    >
                      <td className="px-4 py-2 font-mono text-xs">
                        {row.template}
                      </td>
                      <td className="px-4 py-2 text-right">{row.sent}</td>
                      <td className="px-4 py-2 text-right">{row.pending}</td>
                      <td className="px-4 py-2 text-right">{row.failed}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {isBusy ? <LoadingOverlay /> : null}
    </div>
  );
};

export default StatsPage;
